import { Programs } from '@app/shared/utils/enums/programs.enum';
import {
  getBasicDryRation,
  getCupDryRation,
  getBasicWetRation,
  getBasicTreatsRation,
} from '@app/shared/utils/static-helpers/rationing-helper';
import { Consultation, NutritionDataProduct, NutritionRecommendation, Product, Recommendation, Prediction, Vet } from '@app/core/models';
import { MeasurementCodeType, ProductType, Tool } from '../enums';
import ProductHelper from './product-helper';
import { formatNutritionRations } from './rationing-helper';
import { ConsultationState } from '@app/store/consultation';
import { isNotNullOrUndefined } from '@app/shared/operators/not-null-or-undefined';
import { AllowanceState } from '@app/store/allowance';
import { Helper } from './helper';
import { ExpectedNextVisitData, NextVisitData } from '@app/core/models/visit-next';

export function buildRecommendation(
  products: Product[],
  selectedPackIds: string[],
  nutritionalRecommendation: NutritionRecommendation,
  isLactation: boolean,
): Recommendation {
  const dryRations = nutritionalRecommendation?.dryRations || [];
  const wetRations = nutritionalRecommendation?.wetRations || [];
  const treatsRations = nutritionalRecommendation?.treatsRations || [];

  const getRations = (productType: ProductType) => {
    switch (productType) {
      case ProductType.Dry:
        return { rations: dryRations, basicRation: getBasicDryRation(dryRations), cupRation: getCupDryRation(dryRations) };
      case ProductType.Wet:
        return { rations: wetRations, basicRation: getBasicWetRation(wetRations), cupRation: null };
      case ProductType.Treats:
        return { rations: treatsRations, basicRation: getBasicTreatsRation(treatsRations), cupRation: null };
      default:
        return { rations: [], basicRation: null, cupRation: null };
    }
  };

  return {
    diagnostic: nutritionalRecommendation?.diagnostic,
    nutritionData: products
      .filter((product) => {
        switch (product.productType) {
          case ProductType.Dry:
            return nutritionalRecommendation?.basicDryRation?.quantityPerDay > 0 || isLactation;
          case ProductType.Wet:
            return nutritionalRecommendation?.basicWetRation?.quantityPerDay > 0 || isLactation;
          case ProductType.Treats:
            return nutritionalRecommendation?.basicTreatsRation?.quantityPerDay > 0 || isLactation;
          default:
            return false;
        }
      })
      .map((product) => {
        const { rations, basicRation, cupRation } = getRations(product.productType);
        return {
          product: ProductHelper.formatProductIntoNutritionDataProduct(product, selectedPackIds),
          rations,
          basicRation,
          cupRation,
        };
      }),
  };
}

export function buildConsultationApiBody(consultation: Partial<Consultation> = {}, isRenewal = false): any {
  const { visit = {} } = consultation;
  const { weight, firstWeight } = visit;
  const weightToCreate = {
    bcs: weight?.bcs,
    measure: weight?.measure,
    measureUnit: weight?.measureUnit,
    weightDate: isRenewal ? new Date() : weight?.weightDate,
    type: weight?.type,
  };
  const firstWeightToCreate = {
    bcs: firstWeight?.bcs,
    measure: firstWeight?.measure,
    measureUnit: firstWeight?.measureUnit,
    weightDate: firstWeight?.weightDate,
    type: firstWeight?.type,
  };
  return {
    date: isRenewal ? new Date() : consultation.date,
    tool: consultation.tool,
    comments: consultation.comments,
    renalDetect: consultation.prediction || undefined,
    pathologies: consultation.pathologies,
    sensitivities: consultation.sensitivities,
    targetVisitId: isRenewal ? undefined : consultation.targetVisitId,
    visit: {
      id: isRenewal ? undefined : consultation.id,
      weight: weightToCreate,
      firstWeight: firstWeightToCreate,
      firstVisitDate: visit.firstVisitDate,
      visitDateTime: isRenewal ? new Date() : visit.visitDateTime,
      program: visit.program,
      recommendation: {
        diagnostic: consultation?.visit?.recommendation?.diagnostic,
        nutritionData: consultation?.visit?.recommendation?.nutritionData.map((item) => ({
          product: item.product,
          rations: item.rations,
        })),
      },
    },
  };
}
export function setUndefinedClinicPrice(consultation: Consultation): Consultation {
  return {
    ...consultation,
    visit: {
      ...consultation.visit,
      recommendation: {
        ...consultation.visit.recommendation,
        nutritionData: consultation.visit.recommendation.nutritionData.map((nutrition) => {
          if (isNotNullOrUndefined(nutrition.product.productDetail.package.productPrice.clinicPrice)) {
            return nutrition;
          }

          return {
            ...nutrition,
            product: {
              ...nutrition.product,
              productDetail: {
                ...nutrition.product.productDetail,
                package: {
                  ...nutrition.product.productDetail.package,
                  productPrice: {
                    ...nutrition.product.productDetail.package.productPrice,
                    clinicPrice: -1,
                  },
                },
              },
            },
          };
        }),
      },
    },
  };
}

export function buildConsultationData({
  consultationState,
  allowanceState,
  selectedPackIds,
  prediction,
  products,
  program,
  vet,
  tool,
  currentBigMeasurementUnit,
  isLactation,
}: {
  consultationState: ConsultationState;
  allowanceState: AllowanceState;
  selectedPackIds: string[];
  prediction: Prediction | null;
  products: Product[];
  program: Programs;
  vet: Vet;
  currentBigMeasurementUnit: MeasurementCodeType;
  isLactation: boolean;
  tool?: Tool;
}): Consultation {
  const { lastConsultation, currentConsultationVisitDate } = consultationState;
  const { nutritionRecommendation, allowanceParams: params } = allowanceState;
  if (!params?.deprecatedPetInfo || (!nutritionRecommendation && !isLactation)) return;
  const visitDate = currentConsultationVisitDate || new Date();
  const weight =
    (params.deprecatedPetInfo.weight && {
      weightDate: visitDate,
      bcs: params.deprecatedPetInfo.bcs,
      measure: params.deprecatedPetInfo.weight,
      measureUnit: currentBigMeasurementUnit,
      type: 0,
    }) ||
    null;

  return <Consultation>{
    date: visitDate,
    tool: tool || params.tool,
    vetId: vet?.id,
    visit: {
      /**
       * this visit id is defined by the previous consulation
       */
      id: lastConsultation?.nextVisitId,
      /**
       * If there is already a program started and it matches the current one,
       * we pass the first visit data to the new consultation.
       * It firstVisitDate does not exist in lastConsultation.visit, then it was the first.
       */
      ...(lastConsultation?.visit.program === program
        ? {
            firstVisitDate: lastConsultation.visit.firstVisitDate || lastConsultation.visit.visitDateTime,
            firstWeight: lastConsultation.visit.firstWeight || lastConsultation.visit.weight,
          }
        : /**
           * If the program is different or lastConsultation non existent, this visit becomes the first one
           */
          {
            firstVisitDate: visitDate,
            firstWeight: weight,
          }),
      recommendation: buildRecommendation(products, selectedPackIds, nutritionRecommendation, isLactation),
      /**
       * API requires to force the program name if we're not in a weight manangement program
       */
      program,
      visitDateTime: visitDate,
      weight,
    },
    pathologies: params.deprecatedPetInfo.pathologies,
    sensitivities: params.deprecatedPetInfo.sensitivities,
    prediction,
    /**
     * id of the target visit, passed from lastConsultation
     */
    targetVisitId: lastConsultation?.targetVisitId,
  };
}

export function buildConsultationDataFromExistingConsultation(
  lastConsultation: Partial<Consultation>,
  products: Product[],
  selectedPackIds: string[],
): Partial<Consultation> {
  return {
    ...lastConsultation,
    visit: {
      ...lastConsultation.visit,
      recommendation: {
        ...lastConsultation.visit.recommendation,
        nutritionData: lastConsultation.visit.recommendation.nutritionData
          .filter((item) => !!products.find((product) => product.mainItemId === item.product.mainItemId))
          .map((item) => {
            const matchingProduct = products.find((product) => product.mainItemId === item.product.mainItemId);
            const selectedPack = matchingProduct.packages.find((pack) => selectedPackIds.includes(pack.sCode));
            return {
              rations: item.rations,
              basicRation: item.basicRation,
              cupRation: item.cupRation,
              product: {
                mainItemId: matchingProduct.mainItemId,
                productType: matchingProduct.productType,
                energyCategory: matchingProduct.energyCategory,
                productDetail: {
                  id: matchingProduct.id,
                  ean: matchingProduct.ean,
                  energy: matchingProduct.energy,
                  details: {
                    name: matchingProduct.name,
                    publicUrl: matchingProduct.image,
                  },
                  package: selectedPack && {
                    sCode: selectedPack.sCode,
                    ean: selectedPack.ean,
                    type: selectedPack.type,
                    text: selectedPack.text,
                    weight: selectedPack.weight,
                    measurementUnitCode: selectedPack.measurementUnitCode,
                    productPrice: selectedPack.productPrice,
                  },
                },
              },
            };
          }),
      },
    },
  };
}

export function formatConsultation(consultation: any): Consultation {
  const visit = consultation?.visit;
  const recommendation = visit?.recommendation;

  // TODO recommendation is now correctly formatted, we need to do the same with the rest of consultation
  return {
    ...consultation,
    prediction: consultation?.renalDetect,
    visit: {
      ...(visit || {}),
      recommendation: formatRecommendation(recommendation),
    },
  };
}

export function expectedNextVisitWM(
  expectedNextVisit: NextVisitData,
  weight: number,
  currentBigMeasurementUnit: MeasurementCodeType,
): ExpectedNextVisitData {
  const currentWeight = weight ?? null;
  const intermediateWeight = expectedNextVisit?.intermediateVisit?.measure ?? null;
  const intermediateUnit = expectedNextVisit?.intermediateVisit?.measureUnit ?? currentBigMeasurementUnit;
  const targetWeight = expectedNextVisit?.targetVisit?.measure ?? null;
  const targetUnit = expectedNextVisit?.targetVisit?.measureUnit ?? currentBigMeasurementUnit;
  const nextDate: Date | null = expectedNextVisit?.intermediateVisit?.weightDate
    ? new Date(expectedNextVisit.intermediateVisit.weightDate)
    : null;
  const percentageDiff = Math.abs(Helper.getPercentageDifference(currentWeight, intermediateWeight));
  const recommendedWeeks = Helper.daysToWeeks(Helper.getNumberOfDays(new Date(), nextDate)) || null;

  return {
    currentWeightDisplay: currentWeight !== null ? `${currentWeight.toFixed(2)} ${currentBigMeasurementUnit}` : '- ',
    targetWeightDisplay: targetWeight !== null ? `${targetWeight.toFixed(2)} ${targetUnit}` : '- ',
    nextDate,
    nextDateDisplay: nextDate ?? null,
    nextDateTargetWeightDisplay: intermediateWeight !== null ? `${intermediateWeight.toFixed(2)} ${intermediateUnit}` : '- ',
    diffPercentDisplay: percentageDiff !== null ? `${percentageDiff.toFixed(2)} %` : '- ',
    recommendedWeeks,
  };
}

function formatRecommendation(data: any = {}): Recommendation {
  const nutritionData = data?.nutritionData;
  return {
    diagnostic: data?.diagnostic || {
      kCalAllocation: 0,
      recommendedAllocation: 0,
    },
    nutritionData:
      nutritionData && nutritionData.length
        ? nutritionData.map((item) => {
            const product = formatNutritionProduct(item?.product);
            const rations = formatNutritionRations(item?.rations);
            return {
              ...item,
              product,
              rations,
              basicRation: product.productType === ProductType.Dry ? getBasicDryRation(rations) : getBasicWetRation(rations),
              cupRation: product.productType === ProductType.Dry ? getCupDryRation(rations) : null,
            };
          })
        : [],
  };
}

function formatNutritionProduct(data: any = {}): NutritionDataProduct {
  const productDetail = data?.productDetail;
  const productDetailPackage = productDetail?.package;
  return {
    mainItemId: data.mainItemId || 0,
    productType: data.productType || ProductType.Unknown,
    energyCategory: data.energyCategory || 0,
    productDetail: {
      id: productDetail?.id || '',
      ean: productDetail?.ean,
      energy: productDetail?.energy || 0,
      details: {
        name: productDetail?.details?.name || '',
        publicUrl: productDetail?.details?.publicUrl || '',
      },
      package: {
        sCode: productDetailPackage?.sCode || '',
        ean: productDetailPackage?.ean || '',
        type: productDetailPackage?.type || '',
        text: productDetailPackage?.text || '',
        weight: productDetailPackage?.weight || 0,
        measurementUnitCode: productDetailPackage?.measurementUnitCode,
        productPrice: {
          clinicPrice: productDetailPackage?.clinicProductPrice,
          price: -1,
        },
      },
    },
  };
}
